import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of, throwError, timer } from 'rxjs';
import { map, switchMap, takeWhile, last, catchError, tap } from 'rxjs/operators';
import { BlockBlobClient } from '@azure/storage-blob';
import { API_BASE } from '../api.config';

/** 8 MiB blocks, 4 in parallel — good balance for multi-GB files. */
const BLOCK_SIZE = 8 * 1024 * 1024;
const CONCURRENCY = 4;

/** POST /api/jobs response: job id + direct-upload SAS URLs. */
interface CreateJobResponse {
  jobId: string;
  grUploads: { fileName: string; uploadUrl: string }[];
}

/** GET /api/jobs/{id} response. */
interface JobStatus {
  status: 'awaiting_upload' | 'processing' | 'ready' | 'failed';
  phase?: 'downloading' | 'parsing' | 'saving' | 'ready';
  downloadPercent?: number;
  downloadedBytes?: number;
  totalBytes?: number;
  elapsedSeconds?: number;
  error?: string;
  grRowCount?: number;
  topsRowCount?: number;
}

/**
 * Owns the current job_id and the large-file upload flow:
 *   create job -> browser uploads files DIRECTLY to Blob (SAS) -> process ->
 *   poll until ready.
 *
 * The job_id is the multi-user key: every pipeline request is scoped to it, so
 * concurrent users never collide. It is persisted to sessionStorage so a page
 * refresh keeps the same job.
 */
@Injectable({ providedIn: 'root' })
export class JobService {
  private readonly http = inject(HttpClient);

  private readonly _jobId = signal<string | null>(this.restore());
  readonly jobId = this._jobId.asReadonly();
  readonly hasJob = computed(() => !!this._jobId());

  /** Cumulative bytes uploaded across both files (for a progress bar). */
  private readonly _uploaded = signal(0);
  /** Total bytes to upload across both files. */
  private readonly _total = signal(0);
  readonly uploadedBytes = this._uploaded.asReadonly();
  readonly totalBytes = this._total.asReadonly();
  readonly uploadPercent = computed(() => {
    const total = this._total();
    return total > 0 ? Math.floor((this._uploaded() / total) * 100) : 0;
  });

  /** Backend processing phase, surfaced live to the UI while polling. */
  private readonly _phase = signal<
    'idle' | 'uploading' | 'downloading' | 'parsing' | 'saving' | 'ready'
  >('idle');
  readonly isProcessing = computed(() => {
  const p = this._phase();
  return p !== 'idle' && p !== 'ready';
});
  private readonly _phasePercent = signal(0);
  readonly phase = this._phase.asReadonly();
  readonly phasePercent = this._phasePercent.asReadonly();

  /** Seconds elapsed in the current parse/save phase (from the backend
   *  heartbeat), so the UI shows movement even when there is no percentage. */
  private readonly _phaseElapsed = signal(0);
  readonly phaseElapsed = this._phaseElapsed.asReadonly();

  /** Contextual detail for the current phase: a percentage while transferring,
   *  elapsed time while parsing/saving (which report no percentage). */
  readonly phaseDetail = computed(() => {
    const p = this._phase();
    if (p === 'uploading' || p === 'downloading') {
      return `${this._phasePercent()}%`;
    }
    if (p === 'parsing' || p === 'saving') {
      const s = this._phaseElapsed();
      return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
    }
    return '';
  });

  /** Human-readable label for the current phase. */
  readonly phaseLabel = computed(() => {
    switch (this._phase()) {
      case 'uploading':
        return 'Uploading files to secure storage';
      case 'downloading':
        return 'Backend loading your data';
      case 'parsing':
        return 'Reading & indexing GR log rows';
      case 'saving':
        return 'Preparing results';
      case 'ready':
        return 'Ready';
      default:
        return '';
    }
  });

  /** Base URL for all job-scoped endpoints. Throws if no job yet. */
  base(): string {
    const id = this._jobId();
    if (!id) {
      throw new Error('No active job. Upload files first.');
    }
    return `${API_BASE}/jobs/${id}`;
  }

  /**
   * Full large-file flow. Emits the final ready status. Any failure (create,
   * blob PUT, process, or a "failed" status) propagates as an error.
   *
   * ``location`` (optional) scopes which tops the backend reads for this job to
   * the Location the user picked in the dropdown; omit/empty for "All
   * locations" (no filter), which is the original behaviour.
   */
  run(grFiles: File[], location = ''): Observable<JobStatus> {
    const grTotal = grFiles.reduce((s, f) => s + f.size, 0);
    this._uploaded.set(0);
    this._total.set(grTotal);
    this._phase.set('uploading');
    this._phasePercent.set(0);
    // Track per-file progress (one slot per LAS) so the combined total is
    // accurate.
    const loaded = new Array(grFiles.length).fill(0);
    const bump = () => {
      this._uploaded.set(loaded.reduce((a, b) => a + b, 0));
      this._phasePercent.set(this.uploadPercent());
    };

    const body: { grFiles: string[]; location?: string } = {
      grFiles: grFiles.map((f) => f.name),
    };
    if (location) {
      body.location = location;
    }
    return this.http.post<CreateJobResponse>(`${API_BASE}/jobs`, body).pipe(
      switchMap((job) => {
        this.setJob(job.jobId);
        const uploads = grFiles.map((f, i) =>
          this.putToBlob(job.grUploads[i].uploadUrl, f, (n) => {
            loaded[i] = n;
            bump();
          }),
        );
        return from(Promise.all(uploads)).pipe(map(() => job.jobId));
      }),
      switchMap((jobId) => {
        // Upload done; backend now downloads + parses. Switch to that phase.
        this._phase.set('downloading');
        this._phasePercent.set(0);
        return this.http
          .post(`${API_BASE}/jobs/${jobId}/process`, {})
          .pipe(map(() => jobId));
      }),
      switchMap((jobId) => this.poll(jobId))
    );
  }

  /** Poll status every 3s until it leaves the processing/awaiting states. */
  private poll(jobId: string): Observable<JobStatus> {
    return timer(0, 1500).pipe(
      switchMap(() => this.http.get<JobStatus>(`${API_BASE}/jobs/${jobId}`)),
      tap((s) => this.applyPhase(s)),
      takeWhile(
        (s) => s.status === 'processing' || s.status === 'awaiting_upload',
        true // emit the final (ready/failed) value too
      ),
      last(),
      switchMap((s) =>
        s.status === 'failed'
          ? throwError(() => new Error(s.error ?? 'Processing failed.'))
          : of(s)
      )
    );
  }

  /** Push the backend phase + percent into the live signals for the UI. */
  private applyPhase(s: JobStatus): void {
    if (s.status === 'ready') {
      this._phase.set('ready');
      this._phasePercent.set(100);
      return;
    }
    if (s.phase === 'downloading') {
      this._phase.set('downloading');
      this._phasePercent.set(s.downloadPercent ?? 0);
    } else if (s.phase === 'parsing') {
      this._phase.set('parsing');
      this._phasePercent.set(100);
      this._phaseElapsed.set(s.elapsedSeconds ?? 0);
    } else if (s.phase === 'saving') {
      this._phase.set('saving');
      this._phasePercent.set(100);
      this._phaseElapsed.set(s.elapsedSeconds ?? 0);
    }
  }

  /**
   * Upload a file to Blob Storage using chunked block upload (Put Block +
   * Put Block List). A single PUT is capped at ~5000 MiB and fails with HTTP
   * 413 on larger bodies; the SDK instead splits the file into BLOCK_SIZE
   * blocks uploaded CONCURRENCY at a time, so multi-GB files succeed, run in
   * parallel, and never load fully into memory.
   *
   * `onProgress` reports cumulative bytes transferred (for a progress bar).
   */
  private putToBlob(
    sasUrl: string,
    file: File,
    onProgress?: (loaded: number) => void
  ): Promise<void> {
    const client = new BlockBlobClient(sasUrl);
    return client
      .uploadData(file, {
        blockSize: BLOCK_SIZE,
        concurrency: CONCURRENCY,
        blobHTTPHeaders: {
          blobContentType: file.type || 'application/octet-stream',
        },
        onProgress: onProgress
          ? (ev) => onProgress(ev.loadedBytes)
          : undefined,
      })
      .then(() => undefined);
  }

  private setJob(id: string): void {
    this._jobId.set(id);
    try {
      sessionStorage.setItem('jobId', id);
    } catch {
      /* sessionStorage unavailable (SSR) – ignore */
    }
  }

  clear(): void {
    this._jobId.set(null);
    try {
      sessionStorage.removeItem('jobId');
    } catch {
      /* ignore */
    }
  }

  private restore(): string | null {
    try {
      return sessionStorage.getItem('jobId');
    } catch {
      return null;
    }
  }
}
